import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X, Check } from 'lucide-react'
import { useTags, useCreateTag, useAddTag, useRemoveTag } from '../../hooks/useTags'

const TAG_COLORS = [
  '#10B981', // green
  '#3B82F6', // blue
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#6B7280', // gray
  '#14B8A6', // teal
]

interface TagPickerProps {
  actorId?: string
  selectedTagIds: number[]
  onToggle: (tagId: number) => void
  /** When set, add/remove tags via API mutations directly */
  koblinkId?: string
  compact?: boolean
}

export function TagPicker({ actorId, selectedTagIds, onToggle, koblinkId, compact }: TagPickerProps) {
  const { t } = useTranslation()
  const { data: tags } = useTags(actorId)
  const createTag = useCreateTag()
  const addTag = useAddTag()
  const removeTag = useRemoveTag()

  const [isCreating, setIsCreating] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0])
  const inputRef = useRef<HTMLInputElement>(null)
  const createRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isCreating])

  useEffect(() => {
    if (!isCreating) return
    function handleClickOutside(event: MouseEvent) {
      if (createRef.current && !createRef.current.contains(event.target as Node)) {
        setIsCreating(false)
        setNewTagName('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCreating])

  const handleToggle = async (tagId: number) => {
    const isSelected = selectedTagIds.includes(tagId)
    onToggle(tagId)

    if (koblinkId) {
      try {
        if (isSelected) {
          await removeTag.mutateAsync({ koblinkId, tagId, actorId })
        } else {
          await addTag.mutateAsync({ koblinkId, tagId, actorId })
        }
      } catch {
        // Revert toggle on error
        onToggle(tagId)
      }
    }
  }

  const handleCreate = async () => {
    const name = newTagName.trim()
    if (!name) return
    try {
      const tag = await createTag.mutateAsync({ name, color: selectedColor, actorId })
      onToggle(tag.id)
      if (koblinkId) {
        try {
          await addTag.mutateAsync({ koblinkId, tagId: tag.id, actorId })
        } catch {
          // Tag created but not added to doc
        }
      }
      setNewTagName('')
      setIsCreating(false)
    } catch {
      // Error handled by mutation
    }
  }

  const chipSize = compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-[10px]'

  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {tags?.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id)
          return (
            <button
              key={tag.id}
              onClick={() => handleToggle(tag.id)}
              className={`inline-flex items-center gap-0.5 rounded-full border transition-colors ${chipSize} ${
                isSelected
                  ? 'border-transparent text-white'
                  : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300'
              }`}
              style={
                isSelected
                  ? { backgroundColor: tag.color || '#6B7280' }
                  : undefined
              }
            >
              {!isSelected && (
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color || '#6B7280' }}
                />
              )}
              <span className="truncate max-w-[80px]">{tag.name}</span>
              {isSelected && <X size={8} className="shrink-0 opacity-70" />}
            </button>
          )
        })}

        {/* Create new tag button */}
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className={`inline-flex items-center gap-0.5 rounded-full border border-dashed border-neutral-300 dark:border-neutral-600 text-neutral-400 dark:text-neutral-500 hover:border-primary-400 hover:text-primary-500 transition-colors ${chipSize}`}
          >
            <Plus size={compact ? 8 : 10} />
          </button>
        )}
      </div>

      {/* Inline create form */}
      {isCreating && (
        <div ref={createRef} className="mt-1.5 p-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
          <input
            ref={inputRef}
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
              if (e.key === 'Escape') {
                setIsCreating(false)
                setNewTagName('')
              }
            }}
            placeholder={t('organize.tagNamePlaceholder')}
            className="w-full px-1.5 py-1 text-xs rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1 mb-1.5"
          />
          {/* Color palette */}
          <div className="flex items-center gap-1 mb-1.5">
            {TAG_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setSelectedColor(color)}
                className={`w-4 h-4 rounded-full border-2 transition-colors ${
                  selectedColor === color
                    ? 'border-neutral-900 dark:border-white scale-110'
                    : 'border-transparent hover:border-neutral-300'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="flex justify-end gap-1">
            <button
              onClick={() => {
                setIsCreating(false)
                setNewTagName('')
              }}
              className="px-2 py-1 text-[10px] rounded text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={!newTagName.trim() || createTag.isPending}
              className="px-2 py-1 text-[10px] rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-0.5"
            >
              <Check size={10} />
              {t('common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
